import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { FiMapPin, FiSquare, FiX } from "react-icons/fi"

import { OpenAPI } from "@/client"
import { PaginationFooter } from "@/components/Common/PaginationFooter"
import { VisionCard } from "@/components/VisionUI"
import { isAllOrgs, useOrganization } from "@/contexts/OrganizationContext"
import { useSemanticColors } from "@/hooks/useSemanticColors"

import "leaflet/dist/leaflet.css"
import L from "leaflet"
// Leaflet's default marker icons break under bundlers; restore them the same
// way the tag-detail map does (delete the override so the prototype lookup
// falls through to the merged option URLs).
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet"

// biome-ignore lint/performance/noDelete: removing the override (not =undefined)
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

export const Route = createFileRoute("/_layout/tag-map")({
  component: TagMapPage,
})

interface TagRow {
  id: string
  name: string
  epcid: string
  state?: string | null
  last_lat?: number | null
  last_lon?: number | null
  last_seen_at?: string | null
}

// A located tag (coords guaranteed non-null).
interface LocatedTag extends TagRow {
  lat: number
  lon: number
}

interface Cluster {
  key: string
  lat: number // centroid
  lon: number
  tags: LocatedTag[]
}

const KM_PER_DEG_LAT = 111
// Upper bound requested from the trimmed map endpoint. Matches the backend's
// cap; if an org exceeds it we surface a "capped subset" notice.
const MAP_LIMIT = 120_000
// Page size for the right-hand tag list (keeps the scroll box + adds paging).
const LIST_PAGE_SIZE = 50

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

// Fetch located tags for one org from the trimmed map endpoint in a single
// request. The endpoint already filters to tags with a position and returns
// only the fields the map needs, so there's no client-side paging loop.
async function fetchOrgTags(orgId: string): Promise<TagRow[]> {
  const r = await authedFetch(
    `/api/v1/organizations/${orgId}/tags/map?limit=${MAP_LIMIT}`,
  )
  if (!r.ok) return []
  const body = (await r.json()) as { data?: TagRow[]; count?: number }
  return body.data ?? []
}

// Bucket located tags into a fixed km grid. Cell size in degrees: latitude is
// constant (km/111); longitude is scaled by cos(lat) of the cell's latitude
// band so cells stay ~square on the ground.
function clusterByRadius(tags: LocatedTag[], radiusKm: number): Cluster[] {
  const latStep = radiusKm / KM_PER_DEG_LAT
  const buckets = new Map<string, LocatedTag[]>()
  for (const t of tags) {
    const latCell = Math.floor(t.lat / latStep)
    const bandLat = (latCell + 0.5) * latStep
    const cosLat = Math.max(0.01, Math.cos((bandLat * Math.PI) / 180))
    const lonStep = radiusKm / (KM_PER_DEG_LAT * cosLat)
    const lonCell = Math.floor(t.lon / lonStep)
    const key = `${latCell}:${lonCell}`
    const arr = buckets.get(key)
    if (arr) arr.push(t)
    else buckets.set(key, [t])
  }
  const clusters: Cluster[] = []
  for (const [key, arr] of buckets) {
    const lat = arr.reduce((s, t) => s + t.lat, 0) / arr.length
    const lon = arr.reduce((s, t) => s + t.lon, 0) / arr.length
    clusters.push({ key, lat, lon, tags: arr })
  }
  return clusters
}

// Web Mercator ground resolution constant (metres per pixel at zoom 0, equator).
const MPP_ZOOM0 = 156543.03392
// Target on-screen cluster cell size; bigger = more aggressive clustering.
const AUTO_CLUSTER_PX = 64
// Resolution floor: never cluster tighter than 5 m. This is about as fine as
// the basemap (OSM, ~z19) and the tags' GPS accuracy realistically support —
// going lower just splits the same physical tag on normal GPS jitter.
const MIN_RADIUS_KM = 0.005
// Deepest zoom the map allows. OSM serves native tiles to z19; we overzoom
// (upscale) past that so the user can keep zooming to separate close pins.
const MAX_ZOOM = 19
const MAX_NATIVE_ZOOM = 19

// Cluster cell size derived from the current zoom + latitude: zoom in → cells
// shrink → clusters break apart into individual pins, bottoming out at 10 m.
function autoRadiusKm(zoom: number, lat: number): number {
  const mPerPx = (MPP_ZOOM0 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom
  return Math.max(MIN_RADIUS_KM, (mPerPx * AUTO_CLUSTER_PX) / 1000)
}

// Human-readable resolution label (metres below 1 km, else km).
function fmtRadius(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`
}

// Fits the map to the given bounds whenever fitKey changes, and runs an
// invalidateSize after mount so the freshly-sized container renders tiles.
function FitController({
  bounds,
  fitKey,
}: {
  bounds: L.LatLngBoundsExpression | null
  fitKey: string
}) {
  const map = useMap()
  useEffect(() => {
    const id = setTimeout(() => {
      map.invalidateSize()
      if (bounds) map.fitBounds(bounds, { padding: [40, 40] })
    }, 80)
    return () => clearTimeout(id)
    // biome-ignore lint/correctness/useExhaustiveDependencies: fitKey gates it
  }, [fitKey, map])
  return null
}

// Hands the map instance back to the parent once it's ready, and reports the
// initial viewport so clustering starts from the real zoom/center.
function MapReady({ onReady }: { onReady: (m: L.Map) => void }) {
  const map = useMap()
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current) return
    fired.current = true
    // Fire exactly once on mount. Re-running on every onReady identity change
    // would loop (onReady → setState → re-render → new onReady) and churn the
    // sibling effects, breaking the box-zoom drag.
    onReady(map)
  }, [map, onReady])
  return null
}

// Reports zoom/center/bounds on every zoom or pan so the cluster radius and the
// side list track what the user is actually looking at.
function ViewportWatcher({ onChange }: { onChange: (m: L.Map) => void }) {
  useMapEvents({
    zoomend: (e) => onChange(e.target as L.Map),
    moveend: (e) => onChange(e.target as L.Map),
  })
  return null
}

// Box-zoom on demand (same behaviour as the tag-detail map): while armed,
// panning is disabled and a plain left-drag draws a hollow rectangle; on
// release the map fits to it and the tool disarms. Public Leaflet API only.
function BoxZoomHandler({
  armed,
  onComplete,
}: {
  armed: boolean
  onComplete: () => void
}) {
  const map = useMap()
  useEffect(() => {
    if (!armed) return
    const container = map.getContainer()
    const prevCursor = container.style.cursor
    container.style.cursor = "crosshair"
    map.dragging.disable()
    map.boxZoom.disable()

    let startPt: L.Point | null = null
    let frame: HTMLDivElement | null = null

    const place = (a: L.Point, b: L.Point) => {
      if (!frame) return
      frame.style.left = `${Math.min(a.x, b.x)}px`
      frame.style.top = `${Math.min(a.y, b.y)}px`
      frame.style.width = `${Math.abs(a.x - b.x)}px`
      frame.style.height = `${Math.abs(a.y - b.y)}px`
    }

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      e.preventDefault()
      startPt = map.mouseEventToContainerPoint(e)
      frame = document.createElement("div")
      frame.style.cssText = [
        "position:absolute",
        "border:2px solid #34a1dd",
        "background:rgba(52,161,221,0.10)",
        "pointer-events:none",
        "z-index:900",
        "left:0;top:0;width:0;height:0",
      ].join(";")
      container.appendChild(frame)
      place(startPt, startPt)
    }

    const onMove = (e: MouseEvent) => {
      if (!startPt) return
      place(startPt, map.mouseEventToContainerPoint(e))
    }

    const finish = (e: MouseEvent) => {
      if (!startPt) return
      const endPt = map.mouseEventToContainerPoint(e)
      const dragged =
        Math.abs(endPt.x - startPt.x) > 8 && Math.abs(endPt.y - startPt.y) > 8
      if (frame) {
        frame.remove()
        frame = null
      }
      if (dragged) {
        const bounds = L.latLngBounds(
          map.containerPointToLatLng(startPt),
          map.containerPointToLatLng(endPt),
        )
        map.fitBounds(bounds)
      }
      startPt = null
      onComplete()
    }

    container.addEventListener("mousedown", onDown)
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", finish)
    return () => {
      container.style.cursor = prevCursor
      map.dragging.enable()
      map.boxZoom.enable()
      if (frame) frame.remove()
      container.removeEventListener("mousedown", onDown)
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", finish)
    }
  }, [armed, map, onComplete])
  return null
}

interface MapBoundaryProps {
  children: ReactNode
  message: string
  sc: Record<string, string>
}
class MapBoundary extends Component<MapBoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[TagMapBrowse] crashed:", error, info.componentStack)
  }
  render() {
    if (this.state.error) {
      return (
        <Flex h="600px" align="center" justify="center" p={4}>
          <Text color={this.props.sc.textMuted} fontSize="sm">
            {this.props.message}
          </Text>
        </Flex>
      )
    }
    return this.props.children
  }
}

function TagMapPage() {
  const sc = useSemanticColors()
  const { t } = useTranslation(["tags", "common"])
  const navigate = useNavigate()
  const { currentOrganization, organizations } = useOrganization()
  const orgId = currentOrganization?.id
  const allOrgs = isAllOrgs(currentOrganization)

  const [zoom, setZoom] = useState(4)
  const [centerLat, setCenterLat] = useState(45.5017)
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds | null>(null)
  const [listPage, setListPage] = useState(1)
  const [boxZoomArmed, setBoxZoomArmed] = useState(false)
  // Free-text filter for the right-hand list (name + EPC).
  const [search, setSearch] = useState("")
  // When a cluster (blue circle) is clicked we pin the list to exactly that
  // circle's tags (by id), independent of the viewport, until "Reset filter".
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string> | null>(null)
  const mapRef = useRef<L.Map | null>(null)

  const resetFilter = useCallback(() => {
    setSelectedTagIds(null)
    setSearch("")
    setListPage(1)
  }, [])

  // Cluster radius is driven by the live zoom: zoom in → smaller radius →
  // clusters split into pins, down to a 5 m floor. Latitude is quantized to 1°
  // so panning within a band keeps the grid (and thus marker identities)
  // stable instead of re-bucketing every tag on each pan.
  const quantLat = Math.round(centerLat)
  const radiusKm = useMemo(() => autoRadiusKm(zoom, quantLat), [zoom, quantLat])

  // Single handler for both the initial map and subsequent zoom/pan events.
  const handleViewport = useCallback((m: L.Map) => {
    setZoom(m.getZoom())
    setCenterLat(m.getCenter().lat)
    setViewBounds(m.getBounds())
  }, [])

  // Stable callbacks for the map child components. Inline props here would get
  // a new identity every render; since the children run them inside effects,
  // that churns the effects and (critically) tears down the box-zoom drag
  // mid-gesture. Memoizing keeps those effects mounted across renders.
  const handleMapReady = useCallback(
    (m: L.Map) => {
      mapRef.current = m
      handleViewport(m)
    },
    [handleViewport],
  )
  const disarmBoxZoom = useCallback(() => setBoxZoomArmed(false), [])

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ["tag-map-tags", allOrgs ? "all" : orgId],
    queryFn: async (): Promise<TagRow[]> => {
      if (allOrgs) {
        // Loop the real orgs (skip the "All Organisations" pseudo-entry).
        const realOrgs = organizations.filter((o) => !isAllOrgs(o))
        const all: TagRow[] = []
        for (const o of realOrgs) {
          all.push(...(await fetchOrgTags(o.id)))
        }
        // Dedup by id (same EPC can exist across orgs as distinct rows).
        const seen = new Set<string>()
        return all.filter((tg) => !seen.has(tg.id) && seen.add(tg.id))
      }
      if (!orgId) return []
      return fetchOrgTags(orgId)
    },
    enabled: allOrgs ? organizations.length > 0 : !!orgId,
    staleTime: 60_000,
  })

  // Located vs. total — many tags have no GPS fix.
  const located = useMemo<LocatedTag[]>(
    () =>
      tags
        .filter(
          (tg): tg is TagRow & { last_lat: number; last_lon: number } =>
            tg.last_lat != null &&
            tg.last_lon != null &&
            !(tg.last_lat === 0 && tg.last_lon === 0),
        )
        .map((tg) => ({ ...tg, lat: tg.last_lat, lon: tg.last_lon })),
    [tags],
  )

  // Cluster only the tags on (or just off) screen. This caps the number of
  // rendered Leaflet markers to roughly the visible screen area, independent of
  // how many tags exist — the key to staying smooth at 100K. Off-screen tags
  // never become map layers. Before the first map event we cluster everything,
  // but the initial zoom is wide so that collapses to a handful of clusters.
  const clusters = useMemo(() => {
    const padded = viewBounds?.pad(0.25) ?? null
    const inView = padded
      ? located.filter((t) => padded.contains([t.lat, t.lon]))
      : located
    return clusterByRadius(inView, radiusKm)
  }, [located, radiusKm, viewBounds])

  // Whole-dataset bounds for the initial fit.
  const allBounds = useMemo<L.LatLngBoundsExpression | null>(() => {
    if (located.length === 0) return null
    return L.latLngBounds(located.map((t) => [t.lat, t.lon])).pad(0)
  }, [located])

  // Right-hand list base set: a clicked cluster pins it to exactly that
  // circle's tags; otherwise it tracks the current map viewport (falling back
  // to the whole located set before the first map event fires).
  const baseTags = useMemo(() => {
    if (selectedTagIds) return located.filter((t) => selectedTagIds.has(t.id))
    return viewBounds
      ? located.filter((t) => viewBounds.contains([t.lat, t.lon]))
      : located
  }, [located, viewBounds, selectedTagIds])

  // Free-text search over name + EPC, applied on top of the base set.
  const listTags = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return baseTags
    return baseTags.filter(
      (t) =>
        (t.name ?? "").toLowerCase().includes(q) ||
        t.epcid.toLowerCase().includes(q),
    )
  }, [baseTags, search])

  const isFiltered = selectedTagIds !== null || search.trim() !== ""

  // Paginate the list (the scroll box stays; this adds a page browser below
  // it). Clamp the page so it stays valid when the filtered set shrinks.
  const listTotalPages = Math.max(
    1,
    Math.ceil(listTags.length / LIST_PAGE_SIZE),
  )
  const safeListPage = Math.min(listPage, listTotalPages)
  const pagedListTags = listTags.slice(
    (safeListPage - 1) * LIST_PAGE_SIZE,
    safeListPage * LIST_PAGE_SIZE,
  )

  // The map auto-fits once, to the whole dataset; after that the user's
  // zoom/pan drives clustering, so the fit key only changes with the data.
  const fitKey = `all:${located.length}`

  const center: [number, number] = located.length
    ? [located[0].lat, located[0].lon]
    : [45.5017, -73.5673] // Montréal fallback

  const goToTag = (id: string) =>
    navigate({ to: "/tag-detail", search: { tagId: id } })

  if (!orgId) {
    return (
      <Box>
        <Text color={sc.textMuted}>
          {t("common:org.selectPrompt", "Select an organisation to continue.")}
        </Text>
      </Box>
    )
  }

  return (
    <Box>
      {/* Keep the header left-aligned: a far-right cluster-resolution control
          collides with the fixed top-right assistant chat FAB. */}
      <Flex align="center" gap={3} mb={4} flexWrap="wrap">
        <Heading size="lg" color={sc.heading}>
          {t("tags:map.title", "Tag Map")}
        </Heading>
        {/* Cluster resolution is automatic — derived from the current zoom. */}
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color={sc.textMuted}>
            {t("tags:map.resolution", "Cluster resolution")}
          </Text>
          <Badge colorPalette="blue" size="sm">
            {fmtRadius(radiusKm)}
          </Badge>
        </Flex>
      </Flex>

      <Text color={sc.textMuted} fontSize="sm" mb={4}>
        {t("tags:map.summary", {
          located: located.length,
          defaultValue: "{{located}} located tags",
        })}
        {tags.length >= MAP_LIMIT
          ? ` — ${t("tags:map.capped", "showing a capped subset")}`
          : ""}
      </Text>

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color={sc.spinnerColor} />
        </Flex>
      ) : located.length === 0 ? (
        <VisionCard>
          <Flex direction="column" align="center" py={16} gap={3}>
            <FiMapPin fontSize="32px" color={sc.iconColor} />
            <Text color={sc.textMuted}>
              {t(
                "tags:map.noLocations",
                "No tags have a recorded location yet.",
              )}
            </Text>
          </Flex>
        </VisionCard>
      ) : (
        <Grid templateColumns={{ base: "1fr", lg: "3fr 1fr" }} gap={6}>
          {/* LEFT: map */}
          <VisionCard>
            <Box
              h="600px"
              borderRadius="12px"
              overflow="hidden"
              position="relative"
            >
              {/* Box-zoom button overlay (same as the tag-detail map). */}
              <Box
                as="button"
                position="absolute"
                top="10px"
                right="10px"
                zIndex={1000}
                w="34px"
                h="34px"
                borderRadius="6px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={boxZoomArmed ? "blue.400" : "white"}
                color={boxZoomArmed ? "white" : "#333"}
                border="2px solid rgba(0,0,0,0.2)"
                boxShadow="0 1px 4px rgba(0,0,0,0.3)"
                cursor="pointer"
                title={t("tags:map.boxZoom", "Draw a box to zoom")}
                onClick={() => setBoxZoomArmed((v) => !v)}
              >
                <FiSquare />
              </Box>
              <MapBoundary
                sc={sc}
                message={t(
                  "tags:map.mapFailed",
                  "Map failed to load — see console.",
                )}
              >
                <MapContainer
                  center={center}
                  zoom={4}
                  maxZoom={MAX_ZOOM}
                  style={{ height: "100%", width: "100%" }}
                >
                  <FitController bounds={allBounds} fitKey={fitKey} />
                  <MapReady onReady={handleMapReady} />
                  <ViewportWatcher onChange={handleViewport} />
                  <BoxZoomHandler
                    armed={boxZoomArmed}
                    onComplete={disarmBoxZoom}
                  />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={MAX_ZOOM}
                    maxNativeZoom={MAX_NATIVE_ZOOM}
                  />
                  {clusters.map((c) =>
                    c.tags.length === 1 ? (
                      <Marker key={c.key} position={[c.lat, c.lon]}>
                        <Popup>
                          <TagPopup
                            tag={c.tags[0]}
                            onView={goToTag}
                            viewLabel={t(
                              "tags:map.viewDetails",
                              "View details →",
                            )}
                            stateLabel={t("tags:map.state", "State")}
                          />
                        </Popup>
                      </Marker>
                    ) : (
                      <Marker
                        key={c.key}
                        position={[c.lat, c.lon]}
                        icon={countIcon(c.tags.length)}
                        eventHandlers={{
                          // Click a cluster → fly into its bounds. Zooming in
                          // shrinks the radius, so the cluster breaks apart
                          // into smaller clusters / individual pins.
                          click: () => {
                            const b = L.latLngBounds(
                              c.tags.map(
                                (t) => [t.lat, t.lon] as [number, number],
                              ),
                            )
                            // Pin the list to this circle's tags…
                            setSelectedTagIds(new Set(c.tags.map((t) => t.id)))
                            setListPage(1)
                            // …and fly into them so they split into pins.
                            mapRef.current?.flyToBounds(b, {
                              padding: [40, 40],
                              maxZoom: MAX_ZOOM,
                            })
                          },
                        }}
                      />
                    ),
                  )}
                </MapContainer>
              </MapBoundary>
            </Box>
            {/* Legend + interaction help, mirroring the tag-detail map. */}
            <Box
              mt={3}
              pt={3}
              borderTop="1px solid"
              borderColor="rgba(255,255,255,0.08)"
            >
              <Flex align="center" gap={2} mb={2}>
                <Box
                  w="22px"
                  h="22px"
                  borderRadius="50%"
                  bg="rgba(52,161,221,0.85)"
                  border="2px solid #fff"
                  color="#fff"
                  fontSize="11px"
                  fontWeight="700"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  N
                </Box>
                <Text fontSize="xs" color={sc.textMuted}>
                  {t(
                    "tags:map.legend",
                    "Number = tags clustered at this zoom; click to zoom in and split them. Single tags show a pin.",
                  )}
                </Text>
              </Flex>
              <Box
                as="ul"
                display="flex"
                flexWrap="wrap"
                gap={2}
                listStyleType="none"
                m={0}
                p={0}
              >
                {[
                  t("tags:detail.mapHelpScroll", "Scroll / wheel: zoom"),
                  t("tags:detail.mapHelpPinch", "Pinch: zoom on touch"),
                  t("tags:detail.mapHelpDoubleTap", "Double-tap: zoom in"),
                  t("tags:detail.mapHelpDrag", "Drag: pan"),
                  t("tags:map.boxZoom", "Draw a box to zoom"),
                  t("tags:detail.mapHelpMarker", "Click a pin: details"),
                ].map((tip) => (
                  <Text
                    as="li"
                    key={tip}
                    fontSize="xs"
                    color={sc.textSecondary ?? sc.textMuted}
                    bg="rgba(255,255,255,0.04)"
                    borderRadius="6px"
                    px={2}
                    py={1}
                  >
                    {tip}
                  </Text>
                ))}
              </Box>
            </Box>
          </VisionCard>

          {/* RIGHT: search + filtered list (selected cluster, else viewport). */}
          <VisionCard>
            {/* Reset filter — above the search, right-aligned. */}
            <Flex justify="flex-end" minH="28px" mb={1}>
              {isFiltered && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="blue"
                  onClick={resetFilter}
                >
                  <FiX /> {t("tags:map.resetFilter", "Reset filter")}
                </Button>
              )}
            </Flex>
            {/* Tag search. */}
            <Input
              size="sm"
              mb={2}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setListPage(1)
              }}
              placeholder={t("tags:map.searchPlaceholder", "Search tags…")}
              bg="rgba(255,255,255,0.04)"
              color={sc.heading}
              borderColor={sc.border}
            />
            {/* Count: tags in the list vs. located tags in the org. */}
            <Flex align="center" justify="space-between" mb={2}>
              <Text fontSize="xs" color={sc.textMuted}>
                {t("tags:map.countOf", {
                  shown: listTags.length,
                  total: located.length,
                  defaultValue: "{{shown}} of {{total}} tags",
                })}
              </Text>
              <Badge
                colorPalette={selectedTagIds ? "purple" : "blue"}
                size="sm"
              >
                {selectedTagIds
                  ? t("tags:map.inSelection", "in selection")
                  : viewBounds
                    ? t("tags:map.inView", "in view")
                    : t("tags:map.allList", "all")}
              </Badge>
            </Flex>
            <Box maxH="500px" overflowY="auto" pr={1}>
              <VStack align="stretch" gap={1}>
                {pagedListTags.map((tg) => (
                  <Box
                    as="button"
                    key={tg.id}
                    textAlign="left"
                    p={2}
                    borderRadius="8px"
                    bg="rgba(255,255,255,0.03)"
                    _hover={{ bg: "rgba(255,255,255,0.08)" }}
                    onClick={() => goToTag(tg.id)}
                  >
                    <Text
                      fontSize="sm"
                      color={sc.heading}
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      {tg.name || tg.epcid}
                    </Text>
                    <Text fontSize="xs" color={sc.textMuted} fontFamily="mono">
                      {tg.lat.toFixed(4)}, {tg.lon.toFixed(4)}
                    </Text>
                  </Box>
                ))}
                {listTags.length === 0 && (
                  <Text
                    fontSize="sm"
                    color={sc.textMuted}
                    py={6}
                    textAlign="center"
                  >
                    {t("tags:map.noMatches", "No matching tags.")}
                  </Text>
                )}
              </VStack>
            </Box>
            {listTags.length > LIST_PAGE_SIZE && (
              <PaginationFooter
                page={safeListPage}
                count={listTags.length}
                pageSize={LIST_PAGE_SIZE}
                setPage={setListPage}
              />
            )}
          </VisionCard>
        </Grid>
      )}
    </Box>
  )
}

// A small count badge as a Leaflet divIcon for multi-tag clusters.
function countIcon(count: number) {
  const size = count >= 100 ? 46 : count >= 10 ? 40 : 34
  return L.divIcon({
    html: `<div style="
      width:${size}px;height:${size}px;line-height:${size}px;
      border-radius:50%;text-align:center;font-weight:700;font-size:13px;
      color:#fff;background:rgba(52,161,221,0.85);
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);
    ">${count}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

function TagPopup({
  tag,
  onView,
  viewLabel,
  stateLabel,
}: {
  tag: LocatedTag
  onView: (id: string) => void
  viewLabel: string
  stateLabel: string
}) {
  return (
    <div style={{ fontSize: "12px", lineHeight: 1.5 }}>
      <strong>{tag.name || tag.epcid}</strong>
      <br />
      <span style={{ fontFamily: "monospace" }}>{tag.epcid}</span>
      <br />
      {tag.lat.toFixed(5)}, {tag.lon.toFixed(5)}
      {tag.state ? (
        <>
          <br />
          {stateLabel}: {tag.state}
        </>
      ) : null}
      <br />
      <button
        type="button"
        onClick={() => onView(tag.id)}
        style={{
          marginTop: "6px",
          color: "#34a1dd",
          fontWeight: 600,
          cursor: "pointer",
          background: "none",
          border: "none",
          padding: 0,
        }}
      >
        {viewLabel}
      </button>
    </div>
  )
}
